import React, { useState } from 'react';
import { complianceService } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';

// Use global Papa from CDN to avoid build minification stack overflow
declare const Papa: any;

/** W-002 / US-158 — Texas bulk template (F-301 column contract). */
export const TEXAS_TEMPLATE_CSV =
    'address,bedrooms,rent,txCounty,txCadParcelId,txRegistrationStatus\n' +
    '123 Example St,2,1500,Dallas,DCAD-000111222333,ACTIVE';

/** UK template — byte-stable (existing contract, do not change). */
export const UK_TEMPLATE_CSV =
    'address,postcode,type,bedrooms,rent,gas\n123 Example Street,SW1A 1AA,Residential,2,1500,Yes';

/** Case/space/underscore-insensitive header lookup (txCounty / tax county / TX_COUNTY all match). */
export function pickField(row: Record<string, any>, aliases: string[]): string {
    const normalized: Record<string, any> = {};
    Object.keys(row || {}).forEach((k) => {
        normalized[k.toLowerCase().replace(/[\s_-]/g, '')] = row[k];
    });
    for (const alias of aliases) {
        const v = normalized[alias.toLowerCase().replace(/[\s_-]/g, '')];
        if (v !== undefined && String(v).trim() !== '') return String(v).trim();
    }
    return '';
}

const TEXAS_ALIASES: Record<string, string[]> = {
    address: ['address', 'address_line1', 'Address Line 1'],
    bedrooms: ['bedrooms'],
    rent: ['rent', 'rentAmount', 'Rent'],
    txCounty: ['txCounty', 'tax county', 'county'],
    txCadParcelId: ['txCadParcelId', 'cad parcel id', 'parcel_id', 'parcel'],
    txRegistrationStatus: ['txRegistrationStatus', 'registration status'],
};

export type TexasRowResult =
    | { ok: true; data: { address: { line1: string; postcode?: string }; bedrooms: number; rentAmount: number; txCounty: string; txCadParcelId: string; txRegistrationStatus: string } }
    | { ok: false; error: string };

/**
 * Map one Texas CSV row to the F-301 property payload.
 * Required: address, txCounty, txCadParcelId, txRegistrationStatus —
 * each miss fails the row with the dispatch-mandated `Row N: missing <field>`
 * string. zip is optional and lands in address.postcode (US "Zip Code" label
 * swap — the UK checklist calls the same row "postcode").
 */
export function mapTexasRow(row: Record<string, any>, rowIndex: number): TexasRowResult {
    const n = rowIndex + 1;
    const address = pickField(row, TEXAS_ALIASES.address);
    if (!address) return { ok: false, error: `Row ${n}: missing address` };
    const txCounty = pickField(row, TEXAS_ALIASES.txCounty);
    if (!txCounty) return { ok: false, error: `Row ${n}: missing txCounty` };
    const txCadParcelId = pickField(row, TEXAS_ALIASES.txCadParcelId);
    if (!txCadParcelId) return { ok: false, error: `Row ${n}: missing txCadParcelId` };
    const txRegistrationStatus = pickField(row, TEXAS_ALIASES.txRegistrationStatus);
    if (!txRegistrationStatus) return { ok: false, error: `Row ${n}: missing txRegistrationStatus` };

    const bedroomsRaw = parseInt(pickField(row, TEXAS_ALIASES.bedrooms) || '1', 10);
    const rentRaw = parseFloat(pickField(row, TEXAS_ALIASES.rent) || '0');
    const zip = pickField(row, ['zip', 'Zip Code', 'zipcode']);

    return {
        ok: true,
        data: {
            address: zip ? { line1: address, postcode: zip } : { line1: address },
            bedrooms: Number.isFinite(bedroomsRaw) ? bedroomsRaw : 1,
            rentAmount: Number.isFinite(rentRaw) ? rentRaw : 0,
            txCounty,
            txCadParcelId,
            txRegistrationStatus,
        },
    };
}

// ---------------------------------------------------------------------------
// F-310 — bulk upload contract types (LP src/routes/properties-bulk.js).
// The backend route is the single owner of row validation (its alias map, per
// row). This shared response type is the only surface the TX submit path
// consumes; same shape on dryRun=true and commit.
// ---------------------------------------------------------------------------

export interface BackendResult {
    row: number;
    kind: 'property' | 'tenant';
    mapping: Record<string, string> | null;
    unmappedColumns?: string[];
    status: 'created' | 'would_create' | 'rejected';
    reason?: string;
    propertyId?: string;
    tenantId?: string;
    stableKey?: string | null;
    values?: Record<string, any>;
}

export interface BulkUploadResponse {
    success: boolean;
    dryRun: boolean;
    rowsIn: number;
    created: number;
    rejected: number;
    count?: number;
    message?: string;
    data?: BackendResult[];
    errors?: BackendResult[];
    unmappedColumns?: string[];
}

/** One human line per failing row (dispatch item 3: row number + reason). */
export const rowError = (e: BackendResult): string =>
    `Row ${e.row}: ${e.reason || e.status || 'rejected'}`;

/** Row-level unmapped columns aggregated from the per-row results (falling
 *  back to the response-level list); empty strings and the record_type
 *  discriminator excluded. */
export const unmappedColumnsFrom = (r: BulkUploadResponse): string[] => {
    const perRow = (r.data || []).map((row) => row.unmappedColumns || []);
    const merged = perRow.length ? perRow : [r.unmappedColumns || []];
    return Array.from(
        new Set(merged.flat().map((c) => String(c).trim()).filter((c) => c !== '' && c.toLowerCase() !== 'record_type'))
    );
};

/** Serialize the parsed rows back to CSV for the bulk endpoint, re-emitting
 *  whatever header order the file actually had so the backend maps the same
 *  document it will run alias resolution on. Values are RFC-4180 quoted when
 *  they contain the delimiter, a quote or a newline (defensive parity with
 *  the backend parse — no silent corruption). */
export const texasRowsToCsv = (headers: string[], rows: any[]): string => {
    const aliasesFor = (col: string) => TEXAS_ALIASES[col] || [col];
    const out: string[] = [];
    for (const row of rows) {
        const cells = headers.map((h) => {
            const v = pickField(row, aliasesFor(h));
            return /["\n,]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
        });
        out.push(cells.join(','));
    }
    return [headers.join(','), ...out].join('\n');
};

interface BulkUploadStepProps {
    onBack: () => void;
}

export default function BulkUploadStep({ onBack }: BulkUploadStepProps) {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [report, setReport] = useState<{ total: number; added: number; failed: number; errors: string[] } | null>(null);
    // F-310: two-phase Texas upload. csvText is frozen at preview time so the
    // commit sends the EXACT payload that was previewed (dispatch item 2).
    const [phase, setPhase] = useState<'idle' | 'preview'>('idle');
    const [preview, setPreview] = useState<BulkUploadResponse | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [csvText, setCsvText] = useState<string>('');
    const isTexas = getAppMarket() === 'texas';

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setSuccess(false);
            setReport(null);
            setPhase('idle');
            setPreview(null);
            setPreviewError(null);
            setCsvText('');
        }
    };

    const resetPhase = () => {
        setPhase('idle');
        setPreview(null);
        setPreviewError(null);
        setCsvText('');
    };

    const handleUpload = () => {
        if (!file) return;
        setUploading(true);
        setReport(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results: any) => {
                const rows = results.data as any[];

                if (!isTexas) {
                    // UK lane — unchanged (F-310 touches the Texas submit path
                    // only; the bulk endpoint's alias map resolves neither
                    // postcode nor gas columns, so UK keeps per-row submit).
                    let added = 0;
                    let failed = 0;
                    const errors: string[] = [];

                    for (let i = 0; i < rows.length; i++) {
                        const row = rows[i];
                        const addressVal = row.address || row.address_line1 || row['Address Line 1'] || row['Address'] || '';
                        const postcodeVal = row.postcode || row['Postcode'] || row.zip || '';

                        // Logic for Gas Exception: Default to true unless explicitly 'No', 'False', or '0'
                        // F-310 pre-existing fix: the shipped UK template column is
                        // lowercase `gas` — also read it, not just the 'Gas' variant.
                        const gasVal = row.has_gas || row.hasGasInstallation || row['Gas Installation'] || row.gas || row['Gas'] || 'true';
                        const hasGas = !(['no', 'false', '0', 'n'].includes(gasVal.toString().toLowerCase().trim()));

                        const propertyData = {
                            address: {
                                line1: addressVal,
                                postcode: postcodeVal,
                            },
                            type: row.type || 'Residential',
                            bedrooms: parseInt(row.bedrooms || row['Bedrooms'] || '1', 10),
                            rentAmount: parseFloat(row.rent || row.rentAmount || row['Rent'] || '0'),
                            hasGasInstallation: hasGas
                        };

                        // Basic Validation
                        if (!propertyData.address.line1 || !propertyData.address.postcode) {
                            failed++;
                            errors.push(`Row ${i + 1}: Missing address or postcode`);
                            continue;
                        }

                        try {
                            await complianceService.addProperty(propertyData);
                            added++;
                        } catch (err: any) {
                            failed++;
                            const msg = err.response?.data?.message || err.message || 'Unknown error';
                            errors.push(`Row ${i + 1}: ${msg}`);
                        }
                    }

                    setReport({ total: rows.length, added, failed, errors });
                    setUploading(false);
                    setSuccess(true);
                    return;
                }

                // Texas lane — F-310. Phase 1: ONE ?dryRun=true call against the
                // batch endpoint (backend owns validation, stable-key tenant
                // linkage and row accounting). Nothing server-side is written.
                try {
                    const text = texasRowsToCsv((results.meta?.fields || []) as string[], rows);
                    setCsvText(text);
                    const resp = await complianceService.bulkUploadProperties(text, true);
                    setPreview(resp.data);
                    setPreviewError(null);
                } catch (err: any) {
                    const data = err?.response?.data;
                    // If the server reported anything, still surface it.
                    setPreview(data && data.rowsIn !== undefined ? data : null);
                    setPreviewError(data?.message || err.message || 'Preview failed');
                } finally {
                    setPhase('preview');
                    setUploading(false);
                }
            },
            error: (err: any) => {
                setReport({ total: 0, added: 0, failed: 0, errors: [err.message] });
                setUploading(false);
            }
        });
    };

    /** Phase 2 (F-310): commit the SAME csvText that was previewed. */
    const confirmCommit = async () => {
        if (!csvText) return;
        setUploading(true);
        try {
            const resp = await complianceService.bulkUploadProperties(csvText, false);
            const r = resp.data;
            setReport({
                total: r.rowsIn ?? 0,
                added: r.created,
                failed: r.rejected ?? (r.errors || []).length,
                errors: (r.errors || []).map(rowError),
            });
            setSuccess(true);
            resetPhase();
        } catch (err: any) {
            const data = err?.response?.data;
            if (data && data.rowsIn !== undefined) {
                setReport({
                    total: data.rowsIn,
                    added: data.created,
                    failed: data.rejected ?? (data.errors || []).length,
                    errors: (data.errors || []).map(rowError),
                });
            } else {
                setReport({ total: 0, added: 0, failed: 0, errors: [data?.message || err.message || 'Commit failed'] });
            }
            setSuccess(true);
            resetPhase();
        } finally {
            setUploading(false);
        }
    };

    const rejectionList = preview
        ? (preview.errors && preview.errors.length ? preview.errors : (preview.data || []).filter((r) => r.status === 'rejected'))
        : [];
    const unmapped = preview ? unmappedColumnsFrom(preview) : [];

    return (
        <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Bulk Property Upload</h3>
                <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
            </div>

            <div className="p-6 md:p-8 space-y-6">
                {!success ? (
                    <>
                        <div className="bg-emerald-50 p-6 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between mb-8 border border-emerald-100 shadow-sm">
                            <div className="flex items-start">
                                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                    <i className="fas fa-info-circle text-emerald-600"></i>
                                </div>
                                <div>
                                    <h4 className="text-sm font-black text-emerald-900 uppercase tracking-wide">CSV Preparation Guide</h4>
                                    {isTexas ? (
                                        <>
                                            <p className="text-sm text-emerald-800 mt-1 max-w-xl" data-testid="bulk-guide-texas">
                                                For a successful upload, ensure your file includes <b>Address</b>, <b>txCounty</b>,
                                                <b> txCadParcelId</b> and <b>txRegistrationStatus</b> columns.
                                                Bedrooms, Rent and Zip are optional.
                                            </p>
                                            <div className="grid grid-cols-2 gap-x-8 gap-y-1 mt-3">
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>address</b> (123 Example St)
                                                </div>
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>txCounty</b> (Dallas)
                                                </div>
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>txCadParcelId</b> (DCAD-000111222333)
                                                </div>
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>txRegistrationStatus</b> (ACTIVE)
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500">
                                                    <i className="far fa-circle mr-2 text-[10px]"></i>
                                                    bedrooms (1-5)
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500">
                                                    <i className="far fa-circle mr-2 text-[10px]"></i>
                                                    rent (1500)
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500">
                                                    <i className="far fa-circle mr-2 text-[10px]"></i>
                                                    zip (75201)
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm text-emerald-800 mt-1 max-w-xl" data-testid="bulk-guide-uk">
                                                For a successful upload, ensure your file includes <b>Address</b> and <b>Postcode</b> columns.
                                                You can also include Type, Bedrooms, and Rent.
                                            </p>
                                            <div className="grid grid-cols-2 gap-x-8 gap-y-1 mt-3">
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>address</b> (123 High St)
                                                </div>
                                                <div className="flex items-center text-xs text-emerald-700">
                                                    <i className="fas fa-check-circle mr-2 text-[10px]"></i>
                                                    <b>postcode</b> (SW1A 1AA)
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500">
                                                    <i className="far fa-circle mr-2 text-[10px]"></i>
                                                    type (Residential)
                                                </div>
                                                <div className="flex items-center text-xs text-slate-500">
                                                    <i className="far fa-circle mr-2 text-[10px]"></i>
                                                    bedrooms (1-5)
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    const csvContent = isTexas ? TEXAS_TEMPLATE_CSV : UK_TEMPLATE_CSV;
                                    const blob = new Blob([csvContent], { type: 'text/csv' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = isTexas ? 'texas_property_import_template.csv' : 'property_import_template.csv';
                                    a.click();
                                }}
                                className="mt-4 md:mt-0 px-4 py-2 bg-white text-emerald-700 border border-emerald-200 rounded-lg text-xs font-black shadow-sm hover:bg-emerald-600 hover:text-white transition-all uppercase whitespace-nowrap"
                            >
                                <i className="fas fa-download mr-2 text-[10px]"></i>
                                Download Template
                            </button>
                        </div>

                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:bg-gray-50 transition-colors">
                            <i className="fas fa-file-csv text-4xl text-gray-400 mb-4"></i>
                            <h4 className="text-sm font-medium text-gray-900">Upload CSV File</h4>
                            <p className="text-xs text-gray-500 mt-1 mb-4">Drag and drop or click to select</p>

                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                            />
                        </div>

                        {file && (
                            <div className="bg-emerald-50 p-3 rounded-md flex items-center text-sm text-emerald-800">
                                <i className="fas fa-file-alt mr-2"></i>
                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </div>
                        )}

                        {phase === 'preview' ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                                <div>
                                    <h4 className="text-sm font-black text-gray-900 uppercase tracking-wide">Preview</h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Dry run only — nothing was written. Rows are created when you confirm.
                                    </p>
                                </div>

                                {previewError ? (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="preview-error">
                                        {previewError}
                                        {preview && (
                                            <div className="text-xs text-red-600 mt-1">
                                                Last known counts — rows in {preview.rowsIn}, would_create {preview.created}, rejected {preview.rejected}.
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-3 gap-4 text-center max-w-md">
                                            <div className="bg-white border border-gray-200 rounded-lg py-3">
                                                <div className="text-xs text-gray-500 uppercase tracking-wide">Rows In</div>
                                                <div className="text-xl font-bold text-gray-900" data-testid="preview-rows-in">{preview?.rowsIn}</div>
                                            </div>
                                            <div className="bg-white border border-emerald-200 rounded-lg py-3">
                                                <div className="text-xs text-emerald-600 uppercase tracking-wide">Would Create</div>
                                                <div className="text-xl font-bold text-emerald-600" data-testid="preview-created">{preview?.created}</div>
                                            </div>
                                            <div className="bg-white border border-red-200 rounded-lg py-3">
                                                <div className="text-xs text-red-600 uppercase tracking-wide">Rejected</div>
                                                <div className="text-xl font-bold text-red-600" data-testid="preview-rejected">{preview?.rejected}</div>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-400" data-testid="preview-accounting">
                                            {preview?.rowsIn} rows in = {preview?.created} would_create + {preview?.rejected} rejected
                                        </p>
                                    </>
                                )}

                                {unmapped.length > 0 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                                        <b data-testid="preview-unmapped-title">Unmapped columns</b> (not matched to any import field):{' '}
                                        <span data-testid="preview-unmapped">{unmapped.join(', ')}</span>
                                    </div>
                                )}

                                <div data-testid="row-errors">
                                    <b className="text-xs font-black text-red-700 uppercase tracking-wide">Issues ({rejectionList.length})</b>
                                    {rejectionList.length === 0 ? (
                                        <p className="text-xs text-emerald-700 mt-1" data-testid="row-errors-empty">No rejected rows.</p>
                                    ) : (
                                        <div className="mt-1 p-2 bg-red-50 text-red-700 text-xs rounded max-h-40 overflow-y-auto">
                                            {rejectionList.map((e, i) => (
                                                <div key={i} data-testid="row-error">
                                                    Row {e.row} ({e.kind}): {e.reason || e.status}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={resetPhase}
                                        disabled={uploading || !!previewError}
                                        className="text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors disabled:opacity-50"
                                        data-testid="preview-cancel"
                                    >
                                        Cancel
                                    </button>
                                    {!previewError && (
                                        <button
                                            onClick={confirmCommit}
                                            disabled={uploading || !csvText}
                                            className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                            data-testid="confirm-upload"
                                        >
                                            {uploading ? 'Processing...' : 'Confirm upload (' + (preview?.created ?? 0) + ' rows)'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={handleUpload}
                                    disabled={!file || uploading}
                                    className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                >
                                    {uploading ? 'Processing...' : 'Process CSV'}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-10">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-check text-2xl text-green-600"></i>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">Processing Complete</h3>
                        <div className="mt-4 text-left bg-gray-50 p-4 rounded-lg max-w-md mx-auto">
                            <p><b>Total Rows:</b> {report?.total}</p>
                            <p className="text-green-600"><b>Successfully Added:</b> {report?.added}</p>
                            <p className="text-red-600"><b>Failed:</b> {report?.failed}</p>
                            <div className="text-xs text-gray-500 mt-2" data-testid="row-accounting">
                                {report?.total} rows in = {report?.added} created + {report?.failed} rejected
                            </div>
                            {report?.errors && report.errors.length > 0 && (
                                <div className="mt-2 p-2 bg-red-50 text-red-700 text-xs rounded max-h-32 overflow-y-auto">
                                    {report.errors.map((e, i) => <div key={i}>{e}</div>)}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-center space-x-3 mt-6">
                            <button
                                onClick={() => window.location.href = '/properties'}
                                className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                            >
                                Go to Dashboard
                            </button>
                            <button
                                onClick={onBack}
                                className="text-gray-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                            >
                                Upload More
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
